import React from 'react';
import { object } from 'yup';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useIntl, IntlProvider } from 'react-intl';
import get from 'lodash/get';
import intlPath from '../../../../../../src/web/utils/intlPath';
import styles from './styles.scss';
import withForm from '../../../../../../admin/hocs/withForm';
import Icon from '../../../../../../modules/react/components/stateless/Icon';
import Price from '../../../../../components/stateless/Price';
import Translation from '../../../../../components/stateless/Translation';
import LoadingIndicator from '../../../../../components/stateless/LoadingIndicator';
import FacilityImage from '../../../../../containers/FacilityImage';
import SMMoveInCostBreakdown from '../../../../components/stateless/SMMoveInCostBreakdown';
import FacilitySurchargeBanner from '../../../../components/stateless/FacilitySurchargeBanner';
import SMTooltip from '../../../../components/stateless/SMTooltip';
import { getBinCheckData, getSelectedPaymentMethodType } from '../../../../../../src/web/store/modules/movein';
import { PAYMENT_METHOD_TYPES, RENT_MONTH_NAMES, VALUE_COVERAGE_MONTH_NAMES } from '../constants';

const rentItemRegex = /rent unit/i;
const valueCoverageRegex = /protection plan/i;
const adminFeeRegex = /administrative fee/i;
const lockRegex = /lock/i;
const dateRegex = /[0-9]{1,2}\/[0-9]{1,2}\/[0-9]{4}/;
const webOnlyText = '[WEB ONLY] ';

const propTypes = {
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
    address: PropTypes.shape({
      streetAddress: PropTypes.string.isRequired,
      areaAddress: PropTypes.string.isRequired,
    }).isRequired,
    settings: PropTypes.shape({
      useWeeklyRates: PropTypes.bool.isRequired,
    }).isRequired,
    surchargeEnabled: PropTypes.bool.isRequired,
    surchargePercentage: PropTypes.number.isRequired,
  }),
  showMobileSummary: PropTypes.bool.isRequired,
  setShowMobileSummary: PropTypes.func.isRequired,
  receipt: PropTypes.shape({
    creditAmount: PropTypes.number.isRequired,
    feeAmount: PropTypes.number.isRequired,
    rentAmount: PropTypes.number.isRequired,
    rentMonth: PropTypes.string.isRequired,
    nextDueDate: PropTypes.number.isRequired,
    totalAmount: PropTypes.number.isRequired,
    discountPlans: PropTypes.array().isRequired,
    surchargeAmount: PropTypes.number,
  }),
  receiptLoading: PropTypes.bool.isRequired,
  availableUnit: PropTypes.shape(),
};

const defaultProps = {
  facility: undefined,
  receipt: undefined,
  availableUnit: undefined,
};

const validationSchema = object().shape({});

const initialValues = {};

export const MobileOrderSummary = ({ setShowMobileSummary, facility, availableUnit }) => {
  const { locale } = useIntl();

  if (!facility || !facility.unitGroup) {
    return null;
  }

  const {
    unitGroup,
    settings: { useWeeklyRates },
    currency,
    address: {
      streetAddress,
      areaAddress,
    },
  } = facility;

  const {
    price,
    discountedPrice,
    discountedPriceWeekly,
    priceWeekly,
    isParking,
    width,
    length,
  } = unitGroup;

  const size = isParking
    ? `${width}'x${length}'`
    : `${get(availableUnit, 'width', '')}'x${get(availableUnit, 'length', '')}'`;
  const weeklyRate = (discountedPriceWeekly || priceWeekly);
  const monthlyRate = (discountedPrice || price);
  const unitPrice = useWeeklyRates ? weeklyRate : monthlyRate;

  return (
    <>
      <div className={`${styles.orderSummaryMobile} sticky top-0 z-20 lg:hidden`}>
        <div className="shadow-md border border-gray-200 bg-white mb-4 p-4">
          <div className="flex gap-4 justify-between">
            <div>
              <div className="font-bold text-base leading-4">{streetAddress}</div>
              <div className="mb-2 text-base text-[#565958]">{areaAddress}</div>
            </div>
            <div>
                <button
                  type="button"
                  onClick={() => setShowMobileSummary(true)}
                  className="flex items-center text-underline leading-none p-0"
                >
                  <Translation
                    id="widgets.checkout.summary.title"
                    defaultMessage="View Details"
                  />
                  <Icon id="select-arrow" className="ml-1 rotate-90" />
                </button>
            </div>
          </div>
          <div>
            <span className="font-bold">
              <Translation
                id="widgets.checkout.summary.unit"
                defaultMessage="Unit:"
              />
            </span>
            {' '}
            {size}
            {' - '}
            {!useWeeklyRates ? (
              <>
                <Price
                  currency={currency}
                  locale={locale}
                  value={unitPrice}
                  currencyDisplay="narrowSymbol"
                />
                <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
              </>
            ) : (
              <>
                <Price
                  currency={currency}
                  locale={locale}
                  value={unitPrice}
                  currencyDisplay="narrowSymbol"
                />
                <Translation id="widgets.abbreviation.wk" defaultMessage="/wk" />
                <span className="text-[#565958] ml-1">
                  (<Translation id="widgets.abbreviation.billedmonth" defaultMessage="Billed Monthly" />)
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export const OrderSummaryDetails = ({
  receipt,
  facility,
  showSurchargeAmount,
  showSurchargeDiscount,
}) => {
  const { currency } = facility;
  const { formatMessage, locale, messages } = useIntl();

  if (!receipt) { return null; }

  const { invoiceableItems, discountPlans } = receipt;

  const getItemPrice = item => (item.amount >= 0 ? item.amount : item.subtotal);
  const getDescription = item => item.fullDescription || item.full_description || '';

  const getDisplayDescription = (item) => {
    const desc = getDescription(item);
    const dateResult = dateRegex.exec(desc);
    let label = (item.description || '').replace(webOnlyText, '');
    if (dateResult) {
      const [month] = dateResult[0].split('/');
      const isValueCoverage = valueCoverageRegex.test(desc);
      label = formatMessage(isValueCoverage
        ? VALUE_COVERAGE_MONTH_NAMES[month]
        : RENT_MONTH_NAMES[month]);
    }
    return label;
  };

  const rentItem = invoiceableItems.find(item => rentItemRegex.test(getDescription(item)));
  const lockItem = invoiceableItems.find(item => lockRegex.test(getDescription(item)));
  const adminItem = invoiceableItems.find(item => adminFeeRegex.test(getDescription(item)));
  const otherItems = invoiceableItems.filter((item) => {
    const desc = getDescription(item);
    return !rentItemRegex.test(desc)
      && !lockRegex.test(desc)
      && !adminFeeRegex.test(desc)
      && !valueCoverageRegex.test(desc);
  });

  const rentDiscountAmount = rentItem
    ? (rentItem.discountAmount ?? rentItem.discount_amount ?? 0)
    : 0;
  const rentDisplayAmount = rentItem ? getItemPrice(rentItem) + rentDiscountAmount : 0;
  const feeItems = [lockItem, adminItem].filter(Boolean);

  return (
    <div>
      {rentItem && (
        <div className="flex justify-between mb-3">
          <div>{getDisplayDescription(rentItem)}</div>
          <div><Price currency={currency} locale={locale} value={rentDisplayAmount} /></div>
        </div>
      )}

      {discountPlans?.filter(p => Math.abs(p.amount) > 0).map(plan => (
        <div key={plan.id || plan.name} className="flex justify-between mb-3">
          <div className="flex items-center gap-1 text-[#2a8703]">
            <i className="fa fa-tag" aria-hidden="true" />
            {/* eslint-disable-next-line prefer-template */}
            <Translation id={'widgets.unitstablev2.storageMart.unitPromo.' + plan.name} />
          </div>
          <div className="bg-[#e8f5e9] text-[#2a8703] px-2 py-0.5 rounded font-medium whitespace-nowrap self-center">
            -<Price currency={currency} locale={locale} value={Math.abs(plan.amount)} />
          </div>
        </div>
      ))}

      {feeItems.length > 0 && (
        <div className="mb-3">
          <div className="mb-2">
            <Translation id="widgets.moveInCostBreakdown.oneTimeFees" defaultMessage="One-time Fees" />
          </div>
          <div className="border-l-2 border-gray-300 pl-3 ml-1 flex flex-col gap-2">
            {lockItem && (
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Translation id="widgets.moveInCostBreakdown.lockFee" defaultMessage="Lock" />
                  <SMTooltip
                    body={(
                      <IntlProvider locale={locale} messages={messages}>
                        <Translation
                          id="widgets.checkout.summary.lockDisclaimer"
                          defaultMessage="You may return the unused lock"
                        />
                      </IntlProvider>
                    )}
                  />
                </div>
                <div>
                  <Price
                    currency={currency}
                    locale={locale}
                    value={getItemPrice(lockItem)}
                  />
                </div>
              </div>
            )}
            {adminItem && (
              <div className="flex justify-between">
                <div><Translation id="widgets.moveInCostBreakdown.adminFee" defaultMessage="Admin" /></div>
                <div>
                  <Price
                    currency={currency}
                    locale={locale}
                    value={getItemPrice(adminItem)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {otherItems.map(item => (
        <div key={getDescription(item)} className="flex justify-between mb-3">
          <div>
            {getDisplayDescription(item)}
            {item.quantity > 1 ? ` (${item.quantity})` : null}
          </div>
          <Price currency={currency} locale={locale} value={getItemPrice(item)} />
        </div>
      ))}

      <div className="flex justify-between mb-3">
        <div><Translation id="widgets.checkout.summary.taxes" defaultMessage="Taxes" /></div>
        <div>
          <Price
            currency={currency}
            locale={locale}
            value={receipt.taxAmount || 0}
          />
        </div>
      </div>

      {showSurchargeAmount && (
        <div className="flex justify-between mb-3">
          <div>
            <Translation
              id="widgets.moveInCostBreakdown.processingFee"
              defaultMessage="{surchargePercentage}% Processing Fee"
              values={{ surchargePercentage: facility.surchargePercentage }}
            />
          </div>
          <div>
            <Price
              currency={currency}
              locale={locale}
              value={receipt.surchargeAmount}
            />
          </div>
        </div>
      )}

      {showSurchargeDiscount && (
        <div className="flex justify-between mb-3">
          <div className="text-[#2a8703]">
            <Translation
              id="widgets.checkout.summary.processingFeeDiscount"
              defaultMessage="Processing Fee Discount"
            />
          </div>
          <div className="bg-[#e8f5e9] text-[#2a8703] px-2 py-0.5 rounded font-medium whitespace-nowrap self-center">
            -<Price currency={currency} locale={locale} value={receipt.surchargeAmount} />
          </div>
        </div>
      )}
    </div>
  );
};

const OrderSummary = ({
  facility,
  showMobileSummary,
  setShowMobileSummary,
  receipt,
  receiptLoading,
  availableUnit,
  binCheckData,
  selectedPaymentMethodType,
}) => {
  const { locale } = useIntl();

  if (!facility || !facility.unitGroup || !availableUnit) {
    return null;
  }

  const {
    currency,
    unitGroup,
    surchargeEnabled,
    address: { country },
    settings: { useWeeklyRates },
  } = facility;

  const showSurchargeAmount = surchargeEnabled && (receipt?.surchargeAmount || 0) > 0;
  const showSurchargeDiscount = showSurchargeAmount && (
    binCheckData?.surchargeable === false
    || selectedPaymentMethodType === PAYMENT_METHOD_TYPES.ach
  );

  const {
    price,
    discountedPrice,
    isParking,
    width,
    length,
    promoPrice,
  } = unitGroup;

  const size = isParking ? `${width}'x${length}'` : `${availableUnit.width}'x${availableUnit.length}'`;
  const monthlyRate = (promoPrice || discountedPrice || price);
  const regularMonthlyRate = promoPrice ? discountedPrice : price;

  const totalPrice = receipt ? receipt.totalAmount : null;

  const autoApplyPlan = unitGroup.discountPlans?.find(p => p.autoApply);
  const promoMonths = autoApplyPlan?.discountPlanDiscounts?.length ?? 0;
  const firstMonthDiscount = receipt?.discountPlans?.reduce((sum, p) => (
    sum + Math.abs(p.amount)
  ), 0) ?? 0;
  const fullMonthlyDiscount = discountedPrice
    ? (parseFloat(price) || 0) - (parseFloat(discountedPrice) || 0)
    : 0;
  const totalSavings = receipt && promoMonths > 0 && firstMonthDiscount > 0
    ? firstMonthDiscount + fullMonthlyDiscount * (promoMonths - 1)
    : null;

  return (
    <>
      <div
        className={`${styles.orderSummary} 
    bg-[#fbfaf9] rounded-lg sticky top-24 mt-[32px] text-black lg:block ${showMobileSummary ? `${styles.mobile} rounded-none p-4` : ''}`}
      >
        <button type="button" className={`${styles.arrowIcon} w-8 h-8 flex items-center justify-center mb-2 p-0 lg:hidden`} onClick={() => setShowMobileSummary(false)}>
          <Icon id="times" />
        </button>
        <div className="shadow-md border border-gray-200 bg-white rounded-md mb-4">
          <div className="border-b-1 border-gray-300 p-4">
            <h2 className={`${styles.updateTitle}`}>
              <Translation
                id="widgets.checkout.summary.title"
                defaultMessage="Order Summary"
              />
            </h2>
          </div>
          <div className="border-b border-gray-200 bg-white mb-2 p-4 flex gap-4">
            <div className="w-1/4 max-w-24 sm:w-24 shrink-0">
              <div className="w-full relative pb-[100%]">
                <FacilityImage
                  className="w-full h-full object-cover absolute"
                  facility={facility}
                />
              </div>
            </div>
            <div>
              <div className="font-bold text-base leading-4">{facility.address.streetAddress}</div>
              <div className="mb-2 text-base text-[#565958]">{facility.address.areaAddress}</div>
              <div className="flex flex-col gap-2 mb-2">
                <div>
                  <div>
                    <span className="font-bold">
                      <Translation
                        id="widgets.checkout.summary.unit"
                        defaultMessage="Unit:"
                      />
                    </span>
                    {' '}
                    {size}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4">
            {receiptLoading ? (
              <div className="mb-4 max-w-full h-36">
                <LoadingIndicator />
              </div>
            ) : null }
            { receipt ? (
              <>
                <OrderSummaryDetails
                  receipt={receipt}
                  facility={facility}
                  showSurchargeAmount={showSurchargeAmount}
                  showSurchargeDiscount={showSurchargeDiscount}
                />
                <hr className="my-3 border-gray-300" />
                <div className="flex justify-between font-bold mb-3">
                  <Translation
                    id="widgets.checkout.summary.firsttotaldue"
                    defaultMessage="1st Month Total"
                  />
                  <div>
                    <Price
                      currency={currency}
                      locale={locale}
                      value={showSurchargeAmount && !showSurchargeDiscount
                        && typeof receipt.dueAtMoveInWithSurcharge === 'number'
                        ? receipt.dueAtMoveInWithSurcharge
                        : totalPrice
                      }
                    />
                    {!isParking && <sup>*</sup>}
                  </div>
                </div>
                {totalSavings && (
                  <div className="bg-[#e8f5e9] text-center text-[#2a8703] p-3 rounded mb-3">
                    <Translation
                      id="widgets.moveInCostBreakdown.savingsBanner"
                      defaultMessage="Your {promoMonths} Months Savings {savings}"
                      values={{
                        promoMonths,
                        savings: (
                          <Price currency={currency} locale={locale} value={totalSavings} />
                        ),
                      }}
                    />
                  </div>
                )}
                {!isParking && (
                  <div className="text-xs text-[#565958]">
                    <p>
                      <sup>*</sup>
                      {' '}
                      <Translation
                        id="widgets.moveInCostBreakdown.footnoteTotal"
                        defaultMessage="Rate includes up to two free months of {valueCoverage}."
                        values={{
                          valueCoverage: (
                            <a href={intlPath(locale, '/about-us/value-coverage')} className="!text-[#007a7c] underline">
                              <Translation
                                id="widgets.moveInCostBreakdown.valueCoverage"
                                defaultMessage="Value Coverage"
                              />
                            </a>
                          ),
                        }}
                      />
                    </p>
                  </div>
                )}
              </>
            ) : (
              <SMMoveInCostBreakdown
                monthlyRate={monthlyRate}
                fullMonthlyRate={regularMonthlyRate}
                currency={currency}
                country={country}
                discountPlans={unitGroup.discountPlans}
                surchargeEnabled={surchargeEnabled}
                surchargePercentage={facility.surchargePercentage}
                showDetailedFootnotes={false}
                isParking={unitGroup.isParking}
                useWeeklyRates={useWeeklyRates}
                taxesNote={(
                  <Translation
                    id="widgets.checkout.summary.taxesBeforePayment"
                    defaultMessage="Calculated Before Payment"
                  />
                )}
                useLockTooltip
              />
            )}
          </div>
        </div>
        <FacilitySurchargeBanner facility={facility} />
      </div>
      {showMobileSummary && (
        <button aria-label="close overlay Summary" type="button" className="bg-black fixed inset-0 opacity-0 z-50 opacity-50" onClick={() => setShowMobileSummary(false)} />
      )}
    </>
  );
};

OrderSummary.propTypes = propTypes;
OrderSummary.defaultProps = defaultProps;

const mapStateToProps = state => ({
  binCheckData: getBinCheckData(state),
  selectedPaymentMethodType: getSelectedPaymentMethodType(state),
});

export default connect(mapStateToProps)(withForm({
  mapPropsToValues: () => (
    initialValues
  ),
  validationSchema,
  handleSubmit: (formPayload, formikBag) => formikBag.props.onSubmit(formPayload),
})(OrderSummary));
